import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Step2ResumeUpload = ({ wizardData, updateWizardData, nextStep, prevStep }) => {
  const [file, setFile] = useState(wizardData.resumeFile);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(!!wizardData.resumeText);
  const [hasSavedResume, setHasSavedResume] = useState(false);
  const fileInputRef = useRef(null);

  // Check if there's a saved resume on mount
  useEffect(() => {
    const savedResume = localStorage.getItem('dashworx_saved_resume');
    if (savedResume && wizardData.resumeText) {
      setHasSavedResume(true);
      setUploaded(true);
    }
  }, [wizardData.resumeText]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    console.log('File selected:', selectedFile);
    
    if (!selectedFile) {
      console.log('No file selected');
      return;
    }

    console.log('File details:', {
      name: selectedFile.name,
      type: selectedFile.type,
      size: selectedFile.size
    });

    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(selectedFile.type)) {
      console.error('Invalid file type:', selectedFile.type);
      toast.error('Please upload a PDF or DOCX file');
      return;
    }

    setFile(selectedFile);
    setUploading(true);

    try {
      console.log('Creating FormData...');
      const formData = new FormData();
      formData.append('file', selectedFile);

      console.log('Uploading to:', `${API}/upload-resume`);
      const response = await axios.post(`${API}/upload-resume`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('Upload successful:', {
        textLength: response.data.text?.length,
        filename: response.data.filename
      });

      updateWizardData({
        resumeFile: selectedFile,
        resumeText: response.data.text
      });
      
      // Save resume to localStorage for future use
      localStorage.setItem('dashworx_saved_resume', response.data.text);
      localStorage.setItem('dashworx_saved_resume_filename', selectedFile.name);
      
      setUploaded(true);
      setHasSavedResume(true);
      toast.success('✓ Resume uploaded and saved for future use!');
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(`Failed to upload resume: ${error.response?.data?.detail || error.message || 'Please try again'}`);
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleBrowseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
    }
  };

  const handleNext = () => {
    if (!uploaded) return;
    nextStep();
  };

  const handleClearResume = () => {
    localStorage.removeItem('dashworx_saved_resume');
    localStorage.removeItem('dashworx_saved_resume_filename');
    setHasSavedResume(false);
    setUploaded(false);
    setFile(null);
    updateWizardData({
      resumeFile: null,
      resumeText: ''
    });
    toast.success('Saved resume cleared. Upload a new one.');
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3 sm:mb-4">
          Upload Your Resume
        </h2>
        <p className="text-base sm:text-lg text-muted-foreground">
          {hasSavedResume 
            ? 'Using your previously saved resume. Upload a new one to replace it.' 
            : 'Upload your current resume in PDF or DOCX format (saved for future use)'}
        </p>
      </div>

      {/* Saved Resume Indicator */}
      {hasSavedResume && (
        <div className="bg-emerald-50 dark:bg-emerald-950/20 border-2 border-emerald-500 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-semibold text-emerald-900 dark:text-emerald-100">
                  Resume Loaded ✓
                </p>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  {file?.name || 'Previously saved resume'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearResume}
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-100"
            >
              Upload Different Resume
            </Button>
          </div>
        </div>
      )}

      <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-6 sm:p-12 text-center hover:border-primary transition-colors">
        <input
          ref={fileInputRef}
          type="file"
          id="resume-upload"
          data-testid="resume-upload-input"
          accept=".pdf,.docx"
          onChange={handleFileChange}
          className="hidden"
          disabled={uploading}
        />
        <div onClick={handleBrowseClick} className="cursor-pointer">
          <div className="flex flex-col items-center gap-4">
            {uploaded ? (
              <CheckCircle2 className="w-16 h-16 text-emerald-500" />
            ) : (
              <Upload className="w-16 h-16 text-muted-foreground" />
            )}
            <div>
              <p className="text-lg font-semibold mb-1">
                {uploading ? 'Uploading...' : uploaded ? 'Resume Uploaded!' : 'Click anywhere to upload'}
              </p>
              <p className="text-sm text-muted-foreground">
                {file ? file.name : 'PDF or DOCX (Max 10MB)'}
              </p>
            </div>
            {!uploaded && (
              <div className="mt-2 inline-flex items-center justify-center rounded-lg border border-input bg-background px-8 py-3 text-lg font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground pointer-events-none">
                <FileText className="mr-2 w-5 h-5" />
                Browse Files
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
        <Button
          data-testid="prev-step-btn"
          variant="outline"
          size="lg"
          onClick={prevStep}
          className="h-12 px-6 sm:px-8 font-semibold w-full sm:w-auto order-2 sm:order-1"
        >
          <ArrowLeft className="mr-2 w-5 h-5" />
          Back
        </Button>
        <Button
          data-testid="next-step-btn"
          size="lg"
          onClick={handleNext}
          disabled={!uploaded}
          className="h-12 px-6 sm:px-8 font-semibold transition-all active:scale-95 w-full sm:w-auto order-1 sm:order-2"
        >
          Continue
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </div>
  );
};

export default Step2ResumeUpload;
